import { LeadSource, type Lead } from '@up2stay/up2promo-api-client';
import { formatLeadDisplayName } from '@up2stay/up2promo-commons';

/** Initials for avatar tiles (max 2 chars). */
export function leadInitials(lead: Lead): string {
  const first = lead.firstName?.trim()?.[0] ?? '';
  const last = lead.lastName?.trim()?.[0] ?? '';
  const pair = `${first}${last}`.toUpperCase();
  if (pair) return pair;
  const name = formatLeadDisplayName(lead.firstName, lead.lastName).trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const email = lead.email?.trim();
  if (email) return email.slice(0, 2).toUpperCase();
  return '?';
}

export function personInitials(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

/** Short captured-at label matching mock density (vor 12 Min. / 22.09., 16:07). */
export function formatLeadCapturedAt(
  value: string | Date | null | undefined,
  locale: string,
  nowMs: number = Date.now(),
): string {
  if (value == null) return '';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '';

  const diffMs = nowMs - date.getTime();
  const absMs = Math.abs(diffMs);
  const isDe = locale === 'de';

  if (absMs < 60_000) {
    return isDe ? 'gerade eben' : 'just now';
  }
  if (absMs < 3_600_000) {
    const mins = Math.round(absMs / 60_000);
    return isDe ? `vor ${mins} Min.` : `${mins}m ago`;
  }
  if (absMs < 24 * 3_600_000) {
    const hours = Math.round(absMs / 3_600_000);
    return isDe ? `vor ${hours} Std.` : `${hours}h ago`;
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const sameYear = date.getFullYear() === new Date(nowMs).getFullYear();
  if (sameYear && absMs < 14 * 24 * 3_600_000) {
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}., ${hh}:${mm}`;
  }
  if (sameYear) {
    return `${day}.${month}.`;
  }
  return `${day}.${month}.${String(date.getFullYear()).slice(2)}`;
}

export function leadCompanyLine(lead: Lead, includeJobTitle: boolean): string {
  const company = lead.companyName?.trim() ?? '';
  const job = lead.jobTitle?.trim() ?? '';
  if (includeJobTitle && company && job) return `${company} · ${job}`;
  if (company) return company;
  if (includeJobTitle && job) return job;
  return '';
}

export type LeadSourceVisual = {
  icon: 'form' | 'scan' | 'exchange' | 'manual';
};

export function leadSourceVisual(source: string): LeadSourceVisual {
  switch (source) {
    case LeadSource.PUBLIC_FORM:
    case 'publicForm':
      return { icon: 'form' };
    case LeadSource.BUSINESS_CARD:
      return { icon: 'scan' };
    case LeadSource.CONTACT_EXCHANGE:
      return { icon: 'exchange' };
    default:
      return { icon: 'manual' };
  }
}
